import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  defaultAppSettings,
  getPageTitle,
  isLanguage,
  isTheme,
  normalizeSiteTitle,
  translate,
  type AppLanguage,
  type AppSettings,
  type AppTheme,
  type TranslationKey,
} from "@/lib/app-settings/settings";

type AppSettingsContextValue = AppSettings & {
  setLanguage: (language: AppLanguage) => void;
  setTheme: (theme: AppTheme) => void;
  setSiteTitle: (siteTitle: string) => void;
  setServerSiteSettings: (settings: Partial<Pick<AppSettings, "siteTitle" | "headerTitle" | "headerSubtitle" | "faviconFileId" | "adminLogoFileId">>) => void;
  t: (key: TranslationKey) => string;
};

const storageKey = "vzen-app-settings";
const AppSettingsContext = createContext<AppSettingsContextValue | null>(null);

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings());

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify({ language: settings.language, theme: settings.theme }));
    document.documentElement.lang = settings.language === "zh" ? "zh-CN" : "en";
    document.documentElement.classList.toggle("dark", settings.theme === "dark");
    document.title = getPageTitle(translate("app.name", settings.language), settings.siteTitle);
    updateFavicon(settings.faviconFileId);
  }, [settings]);

  useEffect(() => {
    let active = true;

    async function loadServerSettings() {
      try {
        const response = await fetch("/api/site-settings");

        if (!response.ok) {
          return;
        }

        const payload = (await response.json()) as Partial<AppSettings>;

        if (active) {
          setSettings((current) => ({ ...current, ...payload }));
        }
      } catch {
        // Keep local defaults when the API is unavailable during early boot.
      }
    }

    void loadServerSettings();

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo<AppSettingsContextValue>(
    () => ({
      ...settings,
      setLanguage: (language) => setSettings((current) => ({ ...current, language })),
      setTheme: (theme) => setSettings((current) => ({ ...current, theme })),
      setSiteTitle: (siteTitle) => setSettings((current) => ({ ...current, siteTitle: normalizeSiteTitle(siteTitle) })),
      setServerSiteSettings: (serverSettings) => setSettings((current) => ({ ...current, ...serverSettings })),
      t: (key) => translate(key, settings.language),
    }),
    [settings],
  );

  return <AppSettingsContext.Provider value={value}>{children}</AppSettingsContext.Provider>;
}

export function useAppSettings() {
  const value = useContext(AppSettingsContext);

  if (!value) {
    throw new Error("useAppSettings must be used inside AppSettingsProvider");
  }

  return value;
}

function loadSettings(): AppSettings {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return defaultAppSettings;
    }

    const parsed = JSON.parse(raw) as Partial<AppSettings>;
    return {
      language: isLanguage(parsed.language) ? parsed.language : defaultAppSettings.language,
      theme: isTheme(parsed.theme) ? parsed.theme : defaultAppSettings.theme,
      siteTitle: defaultAppSettings.siteTitle,
      headerTitle: defaultAppSettings.headerTitle,
      headerSubtitle: defaultAppSettings.headerSubtitle,
      faviconFileId: null,
      adminLogoFileId: null,
    };
  } catch {
    return defaultAppSettings;
  }
}

function updateFavicon(fileId: string | null) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }

  link.href = fileId ? `/api/brand-assets/${fileId}` : "/favicon.svg";
}
